import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Send } from 'lucide-react';
import { DataToolbar } from '../../components/common/DataToolbar';
import { DataStateMessage } from '../../components/common/DataStateMessage';
import { Pagination } from '../../components/common/Pagination';
import { showToast } from '../../context/ToastContext';
import {
  getTelegramLinkedUsersService,
  type TelegramLinkedUserItem,
} from '../../service/user';

const PAGE_SIZE = 10;

export const AdminTelegram: React.FC = () => {
  const [items, setItems] = useState<TelegramLinkedUserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const isFirstRender = useRef(true);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getTelegramLinkedUsersService({
        page,
        limit: PAGE_SIZE,
        search: search || undefined,
      });
      setItems(result?.items || []);
      setTotalPages(result?.totalPages || 1);
      setTotalCount(result?.totalCount || 0);
    } catch {
      showToast.error('Không thể tải danh sách Telegram đã liên kết');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    void fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setPage(1);
      setSearch(searchInput);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  return (
    <div className="space-y-4 text-[#e2e8f0]">
      <div className="mb-6 flex items-center gap-3.5">
        <div className="flex h-11 w-11 items-center justify-center rounded-[10px] border border-[#2d4a8a] bg-[#1e2a4a]">
          <Send size={20} className="text-[#3b82f6]" />
        </div>
        <div>
          <h1 className="m-0 text-[18px] font-bold text-[#e2e8f0]">Telegram đã liên kết</h1>
          <p className="m-0 mt-0.5 text-[13px] text-[#64748b]">
            Chỉ hiển thị người dùng đang liên kết Telegram
          </p>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-[#252a38] bg-[#161920]">
        <DataToolbar
          icon={<Send size={16} />}
          title="Người dùng Telegram"
          count={totalCount}
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          searchPlaceholder="Tìm theo username, note hoặc chat ID..."
        />

        <DataStateMessage
          loading={loading}
          empty={!loading && items.length === 0 ? 'Không có user nào đang linked Telegram' : null}
          emptyHint={
            !loading && items.length === 0
              ? 'User unlink Telegram sẽ tự biến mất khỏi danh sách này'
              : undefined
          }
        />

        {!loading && items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="bg-[#10131a]">
                  <th className="px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                    User
                  </th>
                  <th className="px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                    Telegram
                  </th>
                  <th className="px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                    Số tin nhắn
                  </th>
                  <th className="px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                    Linked lúc
                  </th>
                  <th className="px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                    Tin nhắn cuối
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item._id}
                    className="border-t border-[#1e2235] transition-colors hover:bg-[#1c2030]"
                  >
                    <td className="px-5 py-3">
                      <div className="text-[13px] font-semibold text-[#e2e8f0]">{item.username}</div>
                      <div className="text-[11px] text-[#64748b]">{item.note || '—'}</div>
                    </td>
                    <td className="px-5 py-3 font-mono text-[12px] text-[#7dd3fc]">
                      {item.telegramChatId}
                    </td>
                    <td className="px-5 py-3 text-[12px] font-semibold text-[#e2e8f0]">
                      {item.telegramMessageSentCount}
                    </td>
                    <td className="px-5 py-3 text-[12px] text-[#94a3b8]">
                      {item.telegramLinkedAt
                        ? new Date(item.telegramLinkedAt).toLocaleString('vi-VN')
                        : '—'}
                    </td>
                    <td className="px-5 py-3 text-[12px] text-[#94a3b8]">
                      {item.telegramLastMessageAt
                        ? new Date(item.telegramLastMessageAt).toLocaleString('vi-VN')
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default AdminTelegram;
